import React, { useState, useRef, useEffect } from 'react';
import styles from '../../styles/CustomDropdown.module.css';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: string;
  image?: string;
  subtitle?: string;
  group?: string;
}

export interface CustomDropdownProps {
  options: DropdownOption[];
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  searchable?: boolean;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  success?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'full';
  variant?: 'minimal' | 'outlined' | 'filled';
  className?: string;
  maxHeight?: number;
  emptyMessage?: string;
  onSearch?: (query: string) => void;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select an option',
  multiple = false,
  searchable = false,
  disabled = false,
  loading = false,
  error = false,
  success = false,
  size = 'md',
  variant = 'outlined',
  className = '',
  maxHeight = 300,
  emptyMessage = 'No options available',
  onSearch
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Filter options based on search query
  useEffect(() => {
    if (searchable && searchQuery) {
      const filtered = options.filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (option.subtitle && option.subtitle.toLowerCase().includes(searchQuery.toLowerCase()))
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchQuery, options, searchable]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleToggle = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: DropdownOption) => {
    if (option.disabled) return;

    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(option.value)
        ? currentValues.filter(v => v !== option.value)
        : [...currentValues, option.value];
      onChange(newValues);
    } else {
      onChange(option.value);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearch) {
      onSearch(query);
    }
  };

  const removeSelectedItem = (valueToRemove: string) => {
    if (multiple && Array.isArray(value)) {
      const newValues = value.filter(v => v !== valueToRemove);
      onChange(newValues);
    }
  };

  const getDisplayText = () => {
    if (multiple && Array.isArray(value) && value.length > 0) {
      return `${value.length} selected`;
    } else if (!multiple && value) {
      const selectedOption = options.find(option => option.value === value);
      return selectedOption ? selectedOption.label : placeholder;
    }
    return placeholder;
  };

  const isSelected = (optionValue: string) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  const getSelectedOptions = () => {
    if (multiple && Array.isArray(value)) {
      return options.filter(option => value.includes(option.value));
    }
    return [];
  };

  const dropdownClasses = [
    styles.customDropdown,
    styles[`size${size.charAt(0).toUpperCase() + size.slice(1)}`],
    styles[`variant${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
    multiple && styles.multiple,
    searchable && styles.searchable,
    loading && styles.loading,
    disabled && styles.disabled,
    error && styles.error,
    success && styles.success,
    className
  ].filter(Boolean).join(' ');

  const selectClasses = [
    styles.customDropdownSelect,
    isOpen && styles.active
  ].filter(Boolean).join(' ');

  const optionsClasses = [
    styles.customDropdownOptions,
    isOpen && styles.active
  ].filter(Boolean).join(' ');

  return (
    <div ref={dropdownRef} className={dropdownClasses}>
      {/* Select Button */}
      <div className={selectClasses} onClick={handleToggle}>
        {multiple && Array.isArray(value) && value.length > 0 ? (
          <div className={styles.customDropdownSelectedItems}>
            {getSelectedOptions().map(option => (
              <div key={option.value} className={styles.customDropdownSelectedItem}>
                {option.label}
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSelectedItem(option.value);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <span className={styles.customDropdownPlaceholder}>
            {getDisplayText()}
          </span>
        )}
        
        {/* Arrow */}
        <div className={`${styles.customDropdownArrow} ${isOpen ? styles.active : ''}`} />
      </div>

      {/* Dropdown Options */}
      <div 
        className={optionsClasses}
        style={{ maxHeight: isOpen ? `${maxHeight}px` : '0' }}
      >
        {/* Search Input */}
        {searchable && (
          <div className={styles.customDropdownSearch}>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search options..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        )}

        {/* Options List */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => {
            const optionClasses = [
              styles.customDropdownOption,
              isSelected(option.value) && styles.selected,
              option.disabled && styles.disabled
            ].filter(Boolean).join(' ');

            return (
              <div
                key={option.value}
                className={optionClasses}
                onClick={() => handleOptionClick(option)}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Checkbox for multiple selection is handled by CSS ::before and ::after pseudo-elements */}

                {/* Option Icon */}
                {option.icon && (
                  <img src={option.icon} alt="" className={styles.optionIcon} />
                )}

                {/* Option Image */}
                {option.image && (
                  <img src={option.image} alt="" className={styles.optionImage} />
                )}

                {/* Option Content */}
                <div className={styles.optionContent}>
                  <div className={styles.optionTitle}>{option.label}</div>
                  {option.subtitle && (
                    <div className={styles.optionSubtitle}>{option.subtitle}</div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={styles.customDropdownEmpty}>
            <div className={styles.emptyIcon}>🔍</div>
            <div>{emptyMessage}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
